import { sanitizeHtmlContent } from '../sanitizers/sanitizeHtml';
import { encodeCss, validateCssColor, validateCssSize } from './encodeCss';
import { encodeDataAttribute, encodeHtmlAttribute, validateAttributeName } from './encodeHtmlAttribute';
import { encodeEventHandler, encodeJavaScript, encodeJson } from './encodeJavaScript';
import { encodeQueryParam, encodeQueryParams, encodeUrl, validateUrl } from './encodeUrl';

export enum EncodingContext {
  Html = 'html',
  HtmlAttribute = 'html_attribute',
  JavaScript = 'javascript',
  Url = 'url',
  Css = 'css',
  Json = 'json',
  EventHandler = 'event_handler',
  QueryParam = 'query_param',
  DataAttribute = 'data_attribute',
}

/**
 * Encodes a value for a specific context
 * 
 * This is the main function to use when you need context-aware encoding.
 * It automatically applies the appropriate encoding based on where the data will be used.
 * 
 * @param value - The value to encode
 * @param context - The context where the value will be used
 * @returns Encoded value safe for the specified context
 * 
 * @example
 * const userInput = '<script>alert("XSS")</script>';
 */
export function encodeForContext(
  value: any,
  context: EncodingContext
): string {
  if (value == null) {
    return '';
  }

  const strValue = String(value);

  switch (context) {
    case EncodingContext.Html:
      return sanitizeHtmlContent(strValue);

    case EncodingContext.HtmlAttribute:
      return encodeHtmlAttribute(strValue);

    case EncodingContext.JavaScript:
      return encodeJavaScript(strValue);

    case EncodingContext.Url:
      return encodeUrl(strValue);

    case EncodingContext.Css:
      return encodeCss(strValue);

    case EncodingContext.Json:
      return encodeJson(value); // Use original value for JSON

    case EncodingContext.EventHandler:
      return encodeEventHandler(strValue);

    case EncodingContext.QueryParam:
      return encodeQueryParam(strValue);

    case EncodingContext.DataAttribute:
      return encodeDataAttribute(value);

    default:
      return encodeHtmlAttribute(strValue);
  }
}

export {

  // CSS
  encodeCss, encodeDataAttribute, encodeEventHandler, sanitizeHtmlContent as encodeHtml,

  // HTML Attributes
  encodeHtmlAttribute,
  // JavaScript
  encodeJavaScript,
  encodeJson, encodeQueryParam,
  encodeQueryParams,
  // URL
  encodeUrl, validateAttributeName, validateCssColor,
  validateCssSize, validateUrl
};

